import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Mail, Lock } from "lucide-react";
import { SignupSuccessModal } from "./SignupSuccessModal";

interface AuthCompactProps {
  onAuthSuccess: () => void;
}

export const AuthCompact = ({ onAuthSuccess }: AuthCompactProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const { toast } = useToast();

  const getRedirectUrl = () => {
    // Always use the current origin to ensure emails redirect to the correct domain
    return `${window.location.origin}/profile/edit`;
  };

  const handleSignUp = async () => {
    if (!email || !password) {
      toast({
        title: "Error",
        description: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: "Weak Password",
        description: "Password must be at least 6 characters",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const redirectUrl = getRedirectUrl();
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl
        }
      });

      if (error) {
        toast({
          title: "Sign Up Error",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      // Show success modal first
      setShowSuccessModal(true);
      
      // Handle next steps after modal is shown
      setTimeout(() => {
        if (data.user && !data.session) {
          toast({
            title: "Check your email",
            description: "We sent you a confirmation link",
          });
        } else {
          onAuthSuccess();
        }
      }, 500);
    } catch (err) {
      toast({
        title: "Error",
        description: "Something went wrong",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async () => {
    if (!email || !password) {
      toast({
        title: "Error", 
        description: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        if (error.message === "Invalid login credentials") {
          toast({
            title: "Sign In Failed",
            description: "Invalid email or password",
            variant: "destructive",
          });
        } else if (error.message.includes("Email not confirmed")) {
          toast({
            title: "Email Not Verified",
            description: "Please check your email and click the confirmation link",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Sign In Error",
            description: error.message,
            variant: "destructive",
          });
        }
        return;
      }

      if (data.session) {
        onAuthSuccess();
      }
    } catch (err) {
      toast({
        title: "Error",
        description: "Something went wrong",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      <Tabs defaultValue="signin" className="w-full">
        <TabsList className="grid w-full grid-cols-2 h-8">
          <TabsTrigger value="signin" className="text-xs">Sign In</TabsTrigger>
          <TabsTrigger value="signup" className="text-xs">Sign Up</TabsTrigger>
        </TabsList>
        
        <TabsContent value="signin" className="space-y-3 mt-3">
          <div className="space-y-2">
            <Label htmlFor="signin-email" className="text-xs">Email</Label>
            <div className="relative">
              <Mail className="absolute left-2 top-2 h-3 w-3 text-muted-foreground" />
              <Input
                id="signin-email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-7 h-8 text-xs"
                disabled={loading}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="signin-password" className="text-xs">Password</Label>
            <div className="relative">
              <Lock className="absolute left-2 top-2 h-3 w-3 text-muted-foreground" />
              <Input
                id="signin-password"
                type="password"
                placeholder="••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-7 h-8 text-xs"
                disabled={loading}
              />
            </div>
          </div>
          
          <Button 
            onClick={handleSignIn} 
            className="w-full h-8 text-xs bg-green-600 hover:bg-green-700"
            disabled={loading}
          >
            {loading ? "Signing In..." : "Sign In"}
          </Button>
        </TabsContent>

        <TabsContent value="signup" className="space-y-3 mt-3">
          <div className="space-y-2">
            <Label htmlFor="signup-email" className="text-xs">Email</Label>
            <div className="relative">
              <Mail className="absolute left-2 top-2 h-3 w-3 text-muted-foreground" />
              <Input
                id="signup-email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-7 h-8 text-xs"
                disabled={loading}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="signup-password" className="text-xs">Password</Label>
            <div className="relative">
              <Lock className="absolute left-2 top-2 h-3 w-3 text-muted-foreground" />
              <Input
                id="signup-password"
                type="password"
                placeholder="••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-7 h-8 text-xs"
                disabled={loading}
              />
            </div>
          </div>
          
          <Button 
            onClick={handleSignUp} 
            className="w-full h-8 text-xs bg-green-600 hover:bg-green-700"
            disabled={loading}
          >
            {loading ? "Creating Account..." : "Create Account"}
          </Button>
        </TabsContent>
      </Tabs>

      <SignupSuccessModal 
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
      />
    </div>
  );
};